import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../../hooks/useFetch"; 

export default function useDroneGame() {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();
  const userId = authState?.user?._id;
  const userCode = authState?.user?.think5whyscode;
  const userName = authState?.user?.name;
  const token = authState?.token;

  const [screen, setScreen] = useState("home");
  const [difficulty, setDifficulty] = useState("medium");
  const [mode, setMode] = useState("peace");

  const [lastResult, setLastResult] = useState(null); // {score, isNewRecord, packagesCollected}
  const [streak, setStreak] = useState(0);
  const [history, setHistory] = useState([]);
  const [bestRecord, setBestRecord] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  const streakConfirmedToday = useRef(false);

  const fetchHistory = useCallback(async () => {
    if (!userId) return;
    setLoadingHistory(true);
    try {
      const res = await fetchData(
        { url: "/dronedash/history", method: "post", data: { userId }, headers: { Authorization: token } },
        { silent: true }
      );
      if (res?.success) {
        setHistory(res.history || []);
        setBestRecord(res.bestRecord || null);
      }
    } catch (e) {
      console.error("DroneDash history fetch error:", e);
    } finally {
      setLoadingHistory(false);
    }
  }, [userId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeaderboard = useCallback(async () => {
    setLoadingLeaderboard(true);
    try {
      const res = await fetchData(
        { url: "/dronedash/leaderboard", method: "post", headers: { Authorization: token } },
        { silent: true }
      );
      if (res?.success) setLeaderboard(res.leaderboard || []);
    } catch (e) {
      console.error("DroneDash leaderboard fetch error:", e);
    } finally {
      setLoadingLeaderboard(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Confirm today's play once per mount (mirrors EmojiGrid/IQTester —
  // server computes the real streak, client never reports it).
  const confirmStreakForToday = useCallback(async () => {
    if (!userId || streakConfirmedToday.current) return;
    try {
      const res = await fetchData(
        {
          url: "/dronedash/streak/confirm",
          method: "post",
          data: { userId, userCode, userName },
          headers: { Authorization: token },
        },
        { silent: true }
      );
      if (res?.success) {
        setStreak(res.streakDays || 0);
        streakConfirmedToday.current = true;
      }
    } catch (e) {
      console.error("DroneDash streak confirm error:", e);
    }
  }, [userId, userCode, userName, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitScore = useCallback(
    async ({ score, timeTakenSec, packagesCollected }) => {
      if (!userId) return;
      try {
        const res = await fetchData(
          {
            url: "/dronedash/save-score",
            method: "post",
            data: { userId, userCode, userName, score, timeTakenSec, mode, difficulty, packagesCollected },
            headers: { Authorization: token },
          },
          { silent: true }
        );
        if (res?.success) {
          setLastResult({ score, isNewRecord: !!res.isNewRecord, packagesCollected });
        }
      } catch (e) {
        console.error("DroneDash save-score error:", e);
      }
    },
    [userId, userCode, userName, token, mode, difficulty]
  );

  const startGame = useCallback(() => {
    setLastResult(null);
    setScreen("game");
  }, []);

  const endGame = useCallback(
    ({ score, timeTakenSec, packagesCollected }) => {
      submitScore({ score, timeTakenSec, packagesCollected });
      confirmStreakForToday();
      setScreen("results");
    },
    [submitScore, confirmStreakForToday]
  );

  useEffect(() => {
    if (userId) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return {
    screen, setScreen,
    difficulty, setDifficulty,
    mode, setMode,
    lastResult,
    streak,
    history,
    bestRecord,
    leaderboard,
    loadingHistory,
    loadingLeaderboard,
    startGame,
    endGame,
    fetchHistory,
    fetchLeaderboard,
  };
}
